import { RequestListFolderFragmentFragment } from '@app-types/blackroom-api';

export type NestedRequestListFolder = RequestListFolderFragmentFragment & {
  subRows?: RequestListFolderFragmentFragment[];
};

export interface FlatRequestListFolder {
  item: RequestListFolderFragmentFragment;
  depth: number;
}

function buildChildrenMap(items: RequestListFolderFragmentFragment[]) {
  const itemIds = new Set(items.map((i) => i.id));
  const map = new Map<string | null, RequestListFolderFragmentFragment[]>();
  for (const item of items) {
    // A root from the view's perspective is any item whose parent isn't part of
    // the returned set — either parentId is null, or it points outside (e.g. a
    // Dataroom folder that contains the RL tree but isn't itself an RL folder).
    const key =
      item.parentId && itemIds.has(item.parentId) ? item.parentId : null;
    const list = map.get(key);
    if (list) {
      list.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  }
  return map;
}

export function transformToNestedData(
  items: RequestListFolderFragmentFragment[],
): NestedRequestListFolder[] {
  const childrenMap = buildChildrenMap(items);

  function buildTree(parentId: string | null): NestedRequestListFolder[] {
    const children = childrenMap.get(parentId) ?? [];
    return children.map((child) => ({
      ...child,
      subRows: buildTree(child.id),
    }));
  }

  return buildTree(null);
}

export function sortItemsByRank(
  items: RequestListFolderFragmentFragment[],
): RequestListFolderFragmentFragment[] {
  return flattenItemsByRank(items).map(({ item }) => item);
}

export function flattenItemsByRank(
  items: RequestListFolderFragmentFragment[],
): FlatRequestListFolder[] {
  const childrenMap = buildChildrenMap(items);
  const result: FlatRequestListFolder[] = [];
  const visited = new Set<string>();

  function walk(parentId: string | null, depth = 0) {
    const children = childrenMap.get(parentId);
    if (!children) return;

    for (const child of children) {
      if (visited.has(child.id)) continue;

      visited.add(child.id);
      result.push({ item: child, depth });
      walk(child.id, depth + 1);
    }
  }

  walk(null);
  return result;
}
